import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import MonthRangePicker from "@/components/MonthRangePicker";
import { getDefaultDateRange } from "@/analytics/api";
import type { UtilisationFilters, FilterOptions } from "@/analytics/types";

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  searchable?: boolean;
  availableOptions?: string[];
}

export const MultiSelectFilter = ({ label, options, selected, onToggle, searchable = false, availableOptions = options }: MultiSelectFilterProps) => {
  const [open, setOpen] = useState(false);
  const isActive = selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg transition-all duration-150 ${
            isActive
              ? "bg-primary/10 border-primary/50 text-primary hover:bg-primary/15"
              : "hover:bg-accent"
          }`}
        >
          <span className="font-medium">{label}</span>
          {isActive && (
            <Badge
              variant="secondary"
              className="px-1.5 py-0 text-xs min-w-[20px] justify-center bg-primary/20 text-primary border-0"
            >
              {selected.length}
            </Badge>
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map(option => {
                const isSelected = selected.includes(option);
                const unavailable = isSelected && !availableOptions.includes(option);
                return (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => (isSelected || !unavailable) && onToggle(option)}
                  className={`flex items-center gap-2 cursor-pointer ${unavailable ? "text-muted-foreground" : ""}`}
                >
                  <div className={`flex h-4 w-4 items-center justify-center rounded border ${selected.includes(option) ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {selected.includes(option) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="truncate">{option}{unavailable && <span className="block text-[10px]">Selected — no matching data with other active filters.</span>}</span>
                </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface UtilisationFilterBarProps {
  filterOptions: FilterOptions;
  filters: UtilisationFilters;
  onChange: (filters: UtilisationFilters) => void;
  isLoading?: boolean;
}

const UtilisationFilterBar = ({ filterOptions, filters, onChange, isLoading = false }: UtilisationFilterBarProps) => {
  const defaultDateRange = getDefaultDateRange(filterOptions.min_month, filterOptions.max_month);

  const hasActiveDimensionFilters =
    filters.legacy_location.length > 0 ||
    filters.employees.length > 0 ||
    filters.emp_categories.length > 0 ||
    filters.service_lines.length > 0;

  const hasActiveDateFilters =
    filters.date_from !== defaultDateRange.date_from ||
    filters.date_to !== defaultDateRange.date_to;

  const hasActiveFilters = hasActiveDimensionFilters || hasActiveDateFilters;

  const toggle = (key: keyof Pick<UtilisationFilters, "legacy_location" | "employees" | "emp_categories" | "service_lines">, value: string) => {
    const current = filters[key] as string[];
    onChange({
      ...filters,
      [key]: current.includes(value) ? current.filter(v => v !== value) : [...current, value],
    });
  };

  const clearAll = () => {
    const { date_from, date_to } = getDefaultDateRange(filterOptions.min_month, filterOptions.max_month);
    onChange({
      legacy_location: [],
      employees: [],
      emp_categories: [],
      service_lines: [],
      date_from,
      date_to,
    });
  };

  const handleDateChange = (date_from: string | null, date_to: string | null) => {
    onChange({ ...filters, date_from, date_to });
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
      {/* Loading progress bar — sits flush at the very top */}
      <div className="h-0.5 bg-secondary">
        <div
          className={`h-full bg-primary transition-all duration-500 ${
            isLoading ? "opacity-100 w-full animate-pulse" : "opacity-0 w-0"
          }`}
        />
      </div>

      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">

          <MultiSelectFilter
            label="Location"
            options={[...new Set([...filterOptions.legacy_location, ...filters.legacy_location])]}
            availableOptions={filterOptions.legacy_location}
            selected={filters.legacy_location}
            onToggle={v => toggle("legacy_location", v)}
          />
          <MultiSelectFilter
            label="Employee"
            options={[...new Set([...filterOptions.employees, ...filters.employees])]}
            availableOptions={filterOptions.employees}
            selected={filters.employees}
            onToggle={v => toggle("employees", v)}
            searchable
          />
          <MultiSelectFilter
            label="Category"
            options={[...new Set([...filterOptions.emp_categories, ...filters.emp_categories])]}
            availableOptions={filterOptions.emp_categories}
            selected={filters.emp_categories}
            onToggle={v => toggle("emp_categories", v)}
          />
          <MultiSelectFilter
            label="Service Line"
            options={[...new Set([...filterOptions.service_lines, ...filters.service_lines])]}
            availableOptions={filterOptions.service_lines}
            selected={filters.service_lines}
            onToggle={v => toggle("service_lines", v)}
          />

          {/* Vertical divider */}
          <div className="h-5 w-px bg-border/70 mx-1 shrink-0" />

          {filterOptions.min_month && filterOptions.max_month ? (
            <MonthRangePicker
              min_month={filterOptions.min_month}
              max_month={filterOptions.max_month}
              date_from={filters.date_from}
              date_to={filters.date_to}
              onChange={handleDateChange}
            />
          ) : (
            <span className="text-xs text-muted-foreground">Loading…</span>
          )}

          {/* Clear all — right-aligned */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="h-8 gap-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 ml-auto"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtilisationFilterBar;
